import $ from 'jquery';
import get from 'lodash/get';
import omit from 'lodash/omit';
import includes from 'lodash/includes';
import startCase from 'lodash/startCase';
import pick from 'lodash/pick';
import isEmpty from 'lodash/isEmpty';

import {
  getCroppableImageUrlFromAsset
} from 'zoomforth_codesbane_editor/lib/assets.js';

import { 
  getDisplayName as getSectionDisplayName,
  getGridPositionDataForUuid
} from './sections.js';

import {
  FileIcon,
  FormIcon,
  ImageIcon,
  ExternalLinkIcon,
  FilePdfIcon,
  FilePowerPointIcon,
  TextIcon,
  FileVideoishIcon,
  TileIcon,
  VideoPlayIcon
} from 'zoomforth_component_lib/external_modern_icons.jsx';


export const isVideoish = (contentItem)=>
  isVideoishAssetType(contentItem.asset_type);

export const isVideoishAssetType = (assetType)=>
  includes(['youtube', 'video'], assetType);

export const hasCroppableAssetDisplayImage = (asset)=> {
  if (!asset) {
    return false;
  }
  if (asset.isVector) {
    return false;
  }
  if (includes(['video', 'photo'], asset.assetType)) {
    return !!asset.displayImage;
  }
  return false;
};


export const getCroppableImageUrlFromContent = (contentItem, asset)=> {
  // YouTube videos should never try to crop their displayImage due to
  // CORS issues so we enforce that we handle the tile_display_image in
  // this case.
  if (contentItem.asset_type === 'youtube' || contentItem.tile_display_image) {
    return contentItem.tile_display_image;
  } else {
    return getCroppableImageUrlFromAsset(asset);
  }
};



export const canOpenOverlayFor = (section, contentItem)=> {
  if (section.type !== 'tiles') {
    return false;
  }

  const validOverlayContentTypes = [
    'video',
    'photo',
    'youtube',
    'pdf',
    'ppt',
    'html_package',
    'iframe_embed'
  ];

  return includes(validOverlayContentTypes, getContentItemType(contentItem));
};


export const getDisplayableContentDescriptor = (section, contentItem)=> {
  // ContentItem must belong to the specified section or you'll get the wrong descriptor
  if (section.type === 'tiles') {
    return `${getDisplayableTileType(contentItem)} Tile`;
  }
  if (section.type === 'slideshow') {
    return `${getDisplayableTileType(contentItem)} Slide`; // HACK: just use the tile display name maker
  }
  if (section.type === 'videoplaylist') {
    return `${getDisplayableTileType(contentItem)} Playlist Item`; // HACK: just use the tile display name maker
  }
  if (section.type === 'accordion') {
    return getDisplayableTileType(contentItem);
  }

  return getSectionDisplayName(section);

};


export const getContentItemType = (contentItemData)=> {
  const contentItemType = get(contentItemData, 'content_type');
  if (includes(['asset', 'overlay'], contentItemType)) {
    return contentItemData.asset_type;
  }
  return contentItemType;
};


export const getContentItemTypeIconName = (contentItemType)=> {

  switch (contentItemType) {
  case 'text':
    return 'text';

  case 'link':
    return 'web_preview';

  case 'youtube':
  case 'video':
    return 'videoish';

  case 'photo':
    return 'image';

  case 'pdf':
    return 'pdf';

  case 'ppt':
    return 'ppt';

  case 'form':
    return 'form';

  case 'accordion_item':
    return 'accordion_item';

  case 'other':
  default:
    return 'file';

  }
};


export const getDisplayableTileType = (contentItemData)=> {
  const contentItemType = getContentItemType(contentItemData);

  switch (contentItemType) {

  case 'text':
    return 'Text';

  case 'link':
    return 'Web Preview';

  case 'video':
    return 'Video';

  case 'photo':
    return 'Image';

  case 'youtube':
    return 'YouTube';

  case 'pdf':
    return 'PDF';

  case 'ppt':
    return 'Presentation';

  case 'html_package':
    return 'HTML Package';

  case 'iframe_embed':
    return 'Custom Embed';

  case 'form':
    return 'Form';

  case 'other':
    return 'File';
  
  case 'accordion_item':
    return 'Accordion Item';

  default:
    throw new Error(`Unexpected content type; ${contentItemType}`);

  }
};


/**
  Given a pageContentItem object, the section in which that pageContentItem lives and
  a fieldName (caption, tile_caption, etc.) return a human-readable name for that field.

  Due to historical reasons, the field name under which a piece of content data exists may
  not map conceptually to how we display that data to the user. This function attempts
  to codify the true mapping so we can display it.
*/
export const getHumanReadablePageContentItemFieldName = (section, pageContentItem, fieldName)=> {

  if (section.type === 'tiles') {

    if (pageContentItem.content_type === 'text') {
      if (fieldName === 'tile_caption') {
        // Refer to text tiles's captions as "Text Content"
        return 'Text';
      }
    }

    if (pageContentItem.content_type === 'asset') {
      if (includes(['other', 'pdf', 'ppt'], pageContentItem.asset_type)) {
        if (fieldName === 'tile_caption') {
          // Refer to file-like tile's tile_captions as "File Name"
          return 'File Name';
        }
      }
    }

    if (pageContentItem.content_type === 'link') {
      if (fieldName === 'caption') {
        return 'Link Description';
      }
    }
  }
  if (section.type === 'text') {
    return 'Text Content';
  }

  return startCase(fieldName);
};

/**
  Given a pageContentItem object, the section in which that pageContentItem lives 
  return the attribute name on that pageContentItem which would best represents that
  item for a human.

  If there is no useful attribute which can identify this page content item return `null`

  (Due to historical reasons, the field name under which a piece of content data exists may
  not map conceptually to how we display that data to the user. As such the programmer,
  may want to use getHumanReadablePageContentItemFieldName() on the returned value)
*/
export const getHumanIdentifiableAttrForPageContentItem = (section, pageContentItem)=> {
  if (
    section &&
    section.type === 'tiles' &&
    pageContentItem.content_type === 'asset' &&
    includes(['other', 'pdf', 'ppt'], pageContentItem.asset_type)
  ) {
    return 'tile_caption';
  }
  return null;
};


export const getHumanReadableContentDescription = (section, pageContentItem)=> {
  if (section) { // A page content item without a section is a background section image

    const contentItemType = getContentItemType(pageContentItem);

    if (section.type === 'tiles') {

      if (contentItemType === 'text') {
        return 'Text Tile';
      }
      if (contentItemType === 'link') {
        return 'Web Preview Tile';
      }
      if (contentItemType === 'photo') {
        return 'Image Tile';
      }
      if (contentItemType === 'pdf') {
        return 'PDF Tile';
      }
      if (contentItemType === 'ppt') {
        return 'Presentation Tile';
      }
      if (contentItemType === 'other') {
        return 'File Tile';
      }
      if (isVideoishAssetType(contentItemType)) {
        return 'Video Tile';
      }
    }
    
    if (includes(['videoplaylist', 'slideshow'], section.type)) {
      if (isVideoishAssetType(contentItemType)) {
        return 'Video';
      }
      if (contentItemType === 'photo') {
        return 'Image';
      }
    }

  }
  // Some content, like that in a text section doesn't have a reasonable description
  // separate from its section, so we return null
  return null;
};


export const getTileAspectRatioForCropping = (section, contentItem)=> {
  if (section.type !== 'tiles') {
    throw new Error('Cannot get tile crop aspect ratio for non-tiles section');
  }
  return getContentItemType(contentItem) === 'link' ?
      // web preview tiles always should be cropped at 1x1
      { sizex:1, sizey:1 }
    :
      getGridPositionDataForUuid(section, contentItem.uuid);
};


export const isShowingOptimizedImage = (contentItem, asset)=> {
  if (getContentItemType(contentItem) !== 'photo' || !asset.originalImage) {
    return true;
  } else {
    return contentItem.tile_display_type == 'default';
  }
};


export const getSerializableContentDataForCopy = (contentItem)=>
  omit(contentItem, ['id', 'uuid']);


export const getPdfIconUrl = () =>
  'https://d3jozdooylvm2p.cloudfront.net/images/icons/pdf_file_icon.png';

export const getHtmlPackageIconUrl = () =>
  '/static/images/html_package_file_icon.png';

export const getPptIconUrl = () =>
  '/static/images/ppt_file_icon.png';

export const getOtherAssetIconUrl = () =>
  '/static/images/other_file_icon.png';

export const getVideoPlayIcon = () =>
  'https://d3jozdooylvm2p.cloudfront.net/images/icons/play_icon_v2.png';

export const getDefaultIconForTileByItemType = (itemType) => {
  switch (itemType) {
  case 'text':
    return TextIcon();

  case 'link':
    return ExternalLinkIcon();

  case 'video':
    return FileVideoishIcon();

  case 'photo':
    return ImageIcon();

  case 'youtube':
    return VideoPlayIcon();

  case 'pdf':
    return FilePdfIcon();

  case 'ppt':
    return FilePowerPointIcon();

  case 'html_package':
    return FileIcon();

  case 'iframe_embed':
    return FileIcon();

  case 'form':
    return FormIcon();

  case 'other':
    return FileIcon();

  default:
    return TileIcon();
  }
};

export const getDefaultIconForTile = (contentItem) => {
  const contentItemType = getContentItemType(contentItem);
  return getDefaultIconForTileByItemType(contentItemType);
};

export const getContentPayloadForReplace = (contentItem)=> {
  const copyableKeys = [
    'can_download_asset',
    'caption',
    'css_classes_list',
    'overlay_css_classes_list',
    'crop_data',
    'extraData',
    'font_size',
    'horizontal_align',
    'position',
    'renderedCaption',
    'renderedTileCaption',
    'should_show_action_icon',
    'style_preset_classname',
    'thumbnail',
    'tile_action_data',
    'tile_caption',
    'tile_display_image',
    'tile_mobile_order_priority',
    'caption_data',
    'hover_effect_type',
    'hover_background_color',
    'hover_text_color',
    'title',
    'vertical_align',
    'is_hidden_on_mobile'
  ];

  var payload = pick(contentItem, copyableKeys);

  // we special case this because not all content has the same display types.
  // for example if the user chose a slide image from a presentation then
  // replacing the asset with a PDF would have an invalid option.
  if (contentItem.tile_display_type === 'library') {
    payload['tile_display_type'] = contentItem.tile_display_type;
  }

  return payload;
};

export const setTextLinksToOpenInNewPage = (wrapperNodeRef, linkSelector) => {
  $(wrapperNodeRef).find(linkSelector).each(function(index, element){
    var $elem = $(element);
    if (
      $elem.attr('target') != '_parent' &&
      !($elem.attr('target') == '_self' && $elem.attr('href').substring(0, 1) == '#')
    ) {
      $elem.attr('target', '_blank');
    }
  });
};

export const setTextLinksToDisplayExternalOpenMsg = (wrapperNodeRef, linkSelector) => {
  $(wrapperNodeRef).find(linkSelector).each(function(index, element){
    const $elem = $(element);

    const doesOpenInNewTab = (
      $elem.attr('target') != '_parent' &&
      !(
        $elem.attr('target') == '_self' &&
        $elem.attr('href').substring(0, 1) == '#'
      )
    );

    if (doesOpenInNewTab && !$elem.hasClass('zf-external-link')) {
      $elem.addClass('zf-external-link');
      $elem.append('<span class="opens-in-external-view-wrapper">Opens in a new window</span>');
    }
  });
};

export const getCaptionData = (contentItem) => {
  const defaultCaptionData = {
    'orientation': 'underneath',
    'shouldUseEllipsis': false,
    'heightType': 'none',
    'heightValue': 50
  };

  if (!contentItem || (!contentItem && !contentItem.caption_data)) {
    return defaultCaptionData;
  }

  return {
    ...defaultCaptionData,
    ...contentItem.caption_data
  };
};
